import { useCallback, useState } from "react";
import { getFootstepHistory, getFootstepSummary } from "./footstepsApi.js";

const IDLE_STATE = { status: "idle", data: null, error: null };

/**
 * Manages the two on-demand, purpose-logged Prompt 114 reads a single
 * Footstep History query touches: GET .../footsteps (history, requires a
 * validated purpose) and GET .../footsteps/summary (a lighter aggregate
 * over the same worker/range, opened from the result panel's own "เปิดสรุป"
 * action). Neither fetch runs automatically on mount - FootstepHistoryPage
 * only calls runHistoryQuery/runSummaryQuery once its own client-side
 * validation (worker/purpose/range required) has already passed, matching
 * this packet's "no query without purpose" requirement.
 */
export function useFootstepHistory() {
  const [historyState, setHistoryState] = useState(IDLE_STATE);
  const [summaryState, setSummaryState] = useState(IDLE_STATE);

  const runHistoryQuery = useCallback((workerId, params) => {
    setHistoryState({ status: "loading", data: null, error: null });
    // A fresh history query invalidates any summary drawer left open from a
    // previous query - the summary drawer only ever opens from this same
    // result panel's own action, scoped to the query that produced it.
    setSummaryState(IDLE_STATE);
    return getFootstepHistory(workerId, params)
      .then((data) => {
        setHistoryState({ status: "ready", data, error: null });
        return data;
      })
      .catch((error) => {
        setHistoryState({ status: "error", data: null, error });
        throw error;
      });
  }, []);

  const runSummaryQuery = useCallback((workerId, params) => {
    setSummaryState({ status: "loading", data: null, error: null });
    return getFootstepSummary(workerId, params)
      .then((data) => {
        setSummaryState({ status: "ready", data, error: null });
        return data;
      })
      .catch((error) => {
        setSummaryState({ status: "error", data: null, error });
        throw error;
      });
  }, []);

  const resetHistory = useCallback(() => setHistoryState(IDLE_STATE), []);
  const resetSummary = useCallback(() => setSummaryState(IDLE_STATE), []);

  return { historyState, summaryState, runHistoryQuery, runSummaryQuery, resetHistory, resetSummary };
}
